import { ButtonGroup } from '~/shared/forms/controls/base-controls/ButtonGroup';
import { SelectedInterventionList } from './SelectedInterventionList';
import { useGoalsAndInterventionsWizard } from '../GoalsAndInterventionsWizardContext';
import type { SelectedInterventionOption, WizardState } from '../reducer/wizardReducer';
import { NewInterventionSearch } from './NewInterventionSearch';
import { SelectCustomIntervention } from './SelectCustomIntervention';
import { DisciplineOptions, type GoalAndInterventionDefinitionLibraryViewModel } from '@generated';
import { InterventionCard } from '../../interventions/InterventionCard';
import { DisciplineSelector } from '~/libraries/disciplines/DisciplineSelector';

const interventionSelectionOptions = [
	{ id: 'suggested', label: 'Suggested' },
	{ id: 'new', label: 'New' },
	{ id: 'custom', label: 'Custom' }
];

function geUnselectedtSuggestedInterventions(
	wizardState: WizardState,
	goalAndInterventionDefinitionLibrary: GoalAndInterventionDefinitionLibraryViewModel
) {
	const selectedGoal = wizardState.selectedGoal!;
	if (selectedGoal.type === 'existing' || selectedGoal.type === 'custom') {
		return [];
	}
	const goalDefinitionId = selectedGoal.goalDefinition.goalProcedures.map(x => x.procedureConceptId);
	const suggestedInterventions = goalAndInterventionDefinitionLibrary.interventionDefinitions.filter(
		x => x.procedureConceptId && goalDefinitionId.includes(x.procedureConceptId)
	);

	const selectedInterventionProcedureConceptIds = wizardState.selectedInterventions
		.filter(x => x.type === 'predefined')
		.map(x => x.interventionDefinition.procedureConceptId)
		.filter(x => x !== null) as string[];
	return suggestedInterventions.filter(x => x.procedureConceptId && !selectedInterventionProcedureConceptIds.includes(x.procedureConceptId));
}

export function SelectInterventionsStep() {
	const { wizardState, dispatch, goalAndInterventionDefinitionLibrary } = useGoalsAndInterventionsWizard();

	const unselectedSuggestedInterventions = geUnselectedtSuggestedInterventions(wizardState, goalAndInterventionDefinitionLibrary);

	return (
		<div className="grid grid-cols-2 gap-8 px-1 py-2">
			<div className="flex flex-col space-y-4">
				<div className="flex flex-row items-center justify-between">
					<ButtonGroup
						options={interventionSelectionOptions
							.filter(x => x.id !== 'suggested' || unselectedSuggestedInterventions.length > 0)
							.map(x => ({
								...x,
								label: x.id === 'suggested' ? `${x.label} (${unselectedSuggestedInterventions.length})` : x.label
							}))}
						isSelected={(id: string) => wizardState.selectedInterventionOption === id}
						onSelect={(id: string) => dispatch({ type: 'INTERVENTION_OPTION_CHANGED', option: id as SelectedInterventionOption })}
					/>

					{wizardState.selectedInterventionOption === 'new' && (
						<DisciplineSelector
							disciplineOptions={DisciplineOptions}
							selectedDisciplineId={wizardState.selectedInterventionDiscipline}
							setSelectedDisciplineId={disciplineId => dispatch({ type: 'INTERVENTION_DISCIPLINE_CHANGED', disciplineId })}
						/>
					)}
				</div>

				{wizardState.selectedInterventionOption === 'new' ? (
					<NewInterventionSearch />
				) : wizardState.selectedInterventionOption === 'custom' ? (
					<SelectCustomIntervention />
				) : wizardState.selectedInterventionOption === 'suggested' ? (
					<div className="flex flex-col gap-4">
						{unselectedSuggestedInterventions.map(x => {
							return (
								<InterventionCard
									key={x.id}
									interventionDefinition={x}
									selection={{
										selected: false,
										onSelect: () => {
											dispatch({
												type: 'PREDEFINED_INTERVENTION_SELECTED',
												interventionDefinition: x,
												newInterventionId: window.crypto.randomUUID(),
												updateInterventionOption: unselectedSuggestedInterventions.length === 1 ? 'new' : undefined
											});
										}
									}}
								/>
							);
						})}
					</div>
				) : null}
			</div>
			<SelectedInterventionList />
		</div>
	);
}
